import { useState } from 'react';
import type { ReactElement } from 'react';
import { useCalendar } from '../hooks/useCalendar.js';
import type { CalendarEntry, MealSlot } from '../api/calendarTypes.js';
import { useToast } from '../hooks/useToast.js';
import { getRecipe } from '../api/recipes.js';
import type { Recipe } from '../api/types.js';
import {
  createCalendarEntry,
  deleteCalendarEntry,
} from '../api/calendarEntries.js';
import { CalendarHeader } from './calendar/CalendarHeader.js';
import { MonthlyGrid } from './calendar/MonthlyGrid.js';
import { CalendarSkeleton } from './calendar/CalendarSkeleton.js';
import { DayDetailDialog } from './calendar/DayDetailDialog.js';
import { CookingMode } from './calendar/CookingMode.js';
import { EditEntryDialog } from './calendar/EditEntryDialog.js';

export interface CalendarProps {
  onOpenRecipe: (recipeId: number) => void;
}

interface CookingState {
  entry: CalendarEntry;
  recipe: Recipe;
}

export function Calendar({ onOpenRecipe }: CalendarProps): ReactElement {
  const cal = useCalendar();
  const toast = useToast();
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [cookingEntry, setCookingEntry] = useState<CalendarEntry | null>(null);
  const [cookingRecipe, setCookingRecipe] = useState<Recipe | null>(null);
  const [cookingLoading, setCookingLoading] = useState<boolean>(false);
  const [editEntry, setEditEntry] = useState<CalendarEntry | null>(null);

  const dayEntries = cal.entriesByDate.get(cal.selectedDate) ?? [];

  async function startCook(entry: CalendarEntry): Promise<void> {
    setCookingEntry(entry);
    setCookingRecipe(null);
    setCookingLoading(true);
    try {
      const recipe = await getRecipe(entry.recipe.id);
      setCookingRecipe(recipe);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      toast.push('error', `Failed to load recipe: ${message}`);
      setCookingEntry(null);
    } finally {
      setCookingLoading(false);
    }
  }

  function closeCook(): void {
    setCookingEntry(null);
    setCookingRecipe(null);
    setCookingLoading(false);
  }

  async function handleAddMeal(slot: MealSlot, recipeId: number): Promise<void> {
    try {
      await createCalendarEntry({
        date: cal.selectedDate,
        meal_type: slot,
        recipe_id: recipeId,
      });
      toast.push('success', 'Meal added');
      await cal.refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add meal';
      toast.push('error', message);
      throw err;
    }
  }

  async function handleRemoveMeal(entryId: number): Promise<void> {
    try {
      await deleteCalendarEntry(entryId);
      toast.push('success', 'Meal removed');
      await cal.refresh();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove meal';
      toast.push('error', message);
      throw err;
    }
  }

  function openDay(iso: string): void {
    cal.setDate(iso);
    setDetailOpen(true);
  }

  const dialogOpen = cookingEntry !== null;
  const cookingState: CookingState | null =
    cookingEntry && cookingRecipe ? { entry: cookingEntry, recipe: cookingRecipe } : null;

  return (
    <section
      className="flex h-full flex-col"
      data-testid="calendar-view"
    >
      <CalendarHeader
        monthLabel={cal.monthLabel}
        onPrev={cal.goPrevMonth}
        onToday={cal.goToday}
        onNext={cal.goNextMonth}
      />
      <div
        className="flex flex-1 flex-col gap-3 p-3"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)' }}
      >
        {cal.loading && cal.entries.length === 0 ? (
          <CalendarSkeleton />
        ) : (
          <MonthlyGrid cells={cal.monthCells} onSelect={openDay} />
        )}

        {cal.error ? (
          <div
            className="flex flex-col items-center gap-2 rounded-md border p-3 text-sm"
            style={{
              borderColor: 'var(--border)',
              color: 'var(--text-muted)',
              background: 'color-mix(in srgb, #dc2626 8%, transparent)',
            }}
            data-testid="calendar-error"
            role="alert"
          >
            <span>Failed to load agenda: {cal.error}</span>
            <button
              type="button"
              onClick={() => void cal.refresh()}
              className="flex min-h-[40px] items-center justify-center rounded-md border px-3 text-sm font-medium"
              style={{
                borderColor: 'var(--accent)',
                color: 'var(--accent)',
                background: 'transparent',
              }}
              data-testid="calendar-retry"
            >
              Retry
            </button>
          </div>
        ) : null}
      </div>

      <DayDetailDialog
        open={detailOpen}
        date={cal.selectedDate}
        entries={dayEntries}
        summary={cal.summary}
        loading={cal.loading}
        error={cal.error}
        onClose={() => setDetailOpen(false)}
        onOpenRecipe={onOpenRecipe}
        onCook={startCook}
        onEditMeal={(entry) => setEditEntry(entry)}
        onAddMeal={handleAddMeal}
        onRemoveMeal={handleRemoveMeal}
        onRetry={cal.refresh}
        onPrevDay={cal.goPrevDay}
        onNextDay={cal.goNextDay}
      />

      <EditEntryDialog
        open={editEntry !== null}
        entry={editEntry}
        onClose={() => setEditEntry(null)}
        onSaved={async () => {
          toast.push('success', 'Entry updated');
          await cal.refresh();
        }}
        onRemoved={async (id) => {
          toast.push('success', 'Entry removed');
          await cal.refresh();
          void id;
        }}
      />

      <CookingMode
        open={dialogOpen && (cookingLoading || cookingState !== null)}
        recipe={cookingState?.recipe ?? null}
        initialPortions={cookingEntry?.portions ?? 1}
        onClose={closeCook}
      />
      {dialogOpen && cookingLoading && !cookingRecipe ? (
        <div
          className="fixed inset-x-0 top-32 z-50 mx-auto w-fit rounded-md border px-3 py-2 text-sm"
          style={{
            borderColor: 'var(--border)',
            color: 'var(--text-muted)',
            background: 'var(--bg-surface)',
          }}
          data-testid="cooking-loading"
        >
          Loading recipe…
        </div>
      ) : null}
    </section>
  );
}
